// Bulk Media Types
import type fields from './definitions/formFields'
import type { ValidationFunctionName } from './validation'

export type Id = string
export type FieldName = keyof typeof fields
export type FieldValue =
  | TextValue
  | DropDownValue
  | MultiSelectValue
  | MultiTextValue

export interface Field {
  label: string
  name: FieldName
  inputType: string
  fieldAction: FieldAction
  dataType?: 'text' | 'number'
  platform?: 'cat' | 'perkins'
  validations?: [
    {
      type: ValidationFunctionName
      params?: any[]
    },
  ]
}

export type FieldAction = 'replace' | 'append' | null

export interface MultiSelectField extends Field {
  inputType: 'multiselect'
  options: SelectOption[]
}
export interface TextField extends Field {
  inputType: 'text'
}

export interface TextAreaField extends Field {
  inputType: 'textarea'
}

export interface DropDownField extends Field {
  inputType: 'dropdown'
  options: SelectOption[]
}

export interface MultiTextField extends Field {
  inputType: 'multitext'
}

export type FieldValues = {
  [key in FieldName]:
    | TextValue
    | DropDownValue
    | MultiSelectValue
    | MultiTextValue
}

export type TextValue = string
export type DropDownValue = SelectOption | null
export type MultiSelectValue = SelectOption[]
export type MultiTextValue = string[]

export type SelectOptions = Record<FieldName, SelectOption>
export interface SelectOption {
  code: Id
  description: string
  requires?: OptionRequirements
}

export type OptionRequirements = Partial<Record<FieldName, Id[]>>

export type FieldFromServer = {
  fieldName: FieldName
  fieldOptions: SelectOptionFromServer[]
}

export type SelectOptionFromServer = {
  code: Id
  description: string
  requires: FieldFromServer[]
}

export function assignFieldType(field: Field) {
  switch (field.inputType) {
    case 'multiselect':
      return field as MultiSelectField
    case 'textfield':
      return field as TextField
    case 'dropdown':
      return field as DropDownField
    case 'multitext':
      return field as MultiTextField
    case 'textarea':
      return field as TextAreaField
    default:
      console.error(`Unaccounted type: ${field.inputType}`)
      return field as Field
  }
}

export const isMultiTextValue = (value: unknown): value is MultiTextValue =>
  Array.isArray(value) && value.every((v) => typeof v === 'string')

export const isMultiSelectValue = (value: unknown): value is MultiSelectValue =>
  Array.isArray(value) && value.every((v) => typeof v.code === 'string')

export const isTextValue = (value: unknown): value is TextValue =>
  typeof value === 'string'

export const isDropDownValue = (value: unknown): value is DropDownValue =>
  (typeof value === 'object' && value !== null && 'code' in value) ||
  value === null

export type MissingRequirement = {
  option: SelectOption
  fieldName: FieldName
  fieldLabel: Field['label']
  missingField: Field
  missingOptions: SelectOption[]
}

export type BulkEditValidationError = {
  field: string
  errorMessages: string[]
}

// ── API Types ──────────────────────────────────────────────────────────────────

export type BulkFieldsResponse = {
  fields: FieldFromServer[]
}

export type PerformBulkEditRequest = {
  mediaNumbers: Id[]
  mediaType: string
  [key: string]: unknown
}

export type UserCanBulkEditModel = {
  mediaNumbers: Id[]
  mediaType: string
}
